import React from 'react';

type HomeFocusContextValue = {
    /** Registers how to focus search, the default home; called by `ProductPanel`, which owns the field. A later call replaces it. */
    registerFocusHome: (focusHome: () => void) => void;
    /**
     * Makes another control home instead of search until the returned
     * release runs; `ShiftActionButton` claims it for Open Shift while no
     * shift is open. Claims stack: the latest one still held wins.
     */
    claimFocusHome: (focusHome: () => void) => () => void;
    /** Moves focus home: the latest claim still held, else search. A no-op before anything has registered. */
    focusHome: () => void;
    /** Moves focus to search whoever holds home, for a keystroke redirected into it. */
    focusSearch: () => void;
};

const HomeFocusContext = React.createContext<HomeFocusContextValue | null>(
    null,
);

/**
 * Cross-panel signal for "focus goes home" (bos-seller#47's focus model):
 * `ProductPanel` owns the search field and registers how to focus it, so a
 * sibling panel that also needs to send focus home — today the cart, when
 * `Delete` empties it (bos-seller#56) — can call `focusHome()` without
 * holding a ref to a field it doesn't own. While no shift is open, home is
 * the Open-shift control instead (SEL-49): `ShiftActionButton` claims it, and
 * every `focusHome()` lands there until the claim is released.
 */
export const useHomeFocus = (): HomeFocusContextValue => {
    const context = React.useContext(HomeFocusContext);
    if (!context) {
        throw new Error('useHomeFocus must be used within a HomeFocusProvider');
    }
    return context;
};

type HomeFocusProviderProps = {
    children: React.ReactNode;
};

type HomeClaim = { focusHome: () => void };

export const HomeFocusProvider: React.FC<HomeFocusProviderProps> = ({
    children,
}) => {
    const searchRef = React.useRef<() => void>(() => {});
    // Claims sit above search whatever order they arrive in: the top bar's
    // effects run before `ProductPanel` registers.
    const claimsRef = React.useRef<HomeClaim[]>([]);

    const registerFocusHome = React.useCallback((focusHome: () => void) => {
        searchRef.current = focusHome;
    }, []);
    const claimFocusHome = React.useCallback((focusHome: () => void) => {
        // Its own entry, so releasing it never drops another claim of the
        // same handler.
        const claim: HomeClaim = { focusHome };
        claimsRef.current = [...claimsRef.current, claim];
        return () => {
            claimsRef.current = claimsRef.current.filter(
                (held) => held !== claim,
            );
        };
    }, []);
    const focusHome = React.useCallback(() => {
        const top = claimsRef.current[claimsRef.current.length - 1];
        (top?.focusHome ?? searchRef.current)();
    }, []);
    const focusSearch = React.useCallback(() => searchRef.current(), []);

    const value = React.useMemo<HomeFocusContextValue>(
        () => ({ registerFocusHome, claimFocusHome, focusHome, focusSearch }),
        [registerFocusHome, claimFocusHome, focusHome, focusSearch],
    );

    return (
        <HomeFocusContext.Provider value={value}>
            {children}
        </HomeFocusContext.Provider>
    );
};
